// @mui
import {
  Stack,
  Grid,
  Button,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Tabs,
  Tab,
} from '@mui/material';
// hooks
import { useEffect, useState } from 'react';
import HeaderBreadcrumbs from 'src/components/HeaderBreadcrumbs';
import Iconify from 'src/components/Iconify';
import Image from 'src/components/Image';
import TextMaxLine from 'src/components/TextMaxLine';
import ConfirmationDialog from '../../shared/ConfirmationDialog';
import useIsFullAdmin from 'src/hooks/useIsFullAdmin';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { fDate } from 'src/utils/formatTime';
import { SuccessStoryModal } from './SuccessStoryModal';
import { useSuccessStories } from '../useSuccessStories';
import { SuccessStory } from 'src/@types/successStory';

type FilterType = 'all' | 'story' | 'activity';

// ----------------------------------------------------------------------

export default function SuccessStoriesList() {
  const {
    stories,
    modalOpen,
    setModalOpen,
    currentStory,
    setCurrentStory,
    getList,
    handleEditRow,
    handleDeleteRow,
  } = useSuccessStories();

  const [confirmOpen, setConfirmOpen] = useState({ open: false, currentId: '' });
  // DELETE is admin-only on the backend; create/edit are open to customer-care
  // and sales.
  const canDelete = useIsFullAdmin();
  const [filter, setFilter] = useState<FilterType>('all');

  useEffect(() => {
    getList().catch(console.error);
  }, []);

  const filteredStories = stories.filter((story: SuccessStory) => {
    if (filter === 'all') return true;
    const storyType = story.type || 'story';
    return storyType === filter;
  });

  return (
    <Stack>
      <HeaderBreadcrumbs
        heading="Success Stories"
        links={[
          { name: 'Dashboard', href: PATH_DASHBOARD.root },
          { name: 'Success Stories', href: PATH_DASHBOARD.successStories },
        ]}
        action={
          <Button
            variant="contained"
            onClick={() => {
              setCurrentStory(undefined);
              setModalOpen(true);
            }}
            startIcon={<Iconify icon={'eva:plus-fill'} />}
          >
            New Story
          </Button>
        }
      />

      <Tabs
        value={filter}
        onChange={(_, value) => setFilter(value)}
        sx={{ mb: 2 }}
      >
        <Tab value="all" label="All" />
        <Tab value="story" label="Stories" />
        <Tab value="activity" label="Activities" />
      </Tabs>

      <Grid container spacing={2}>
        {filteredStories.map((story: SuccessStory) => (
          <Grid key={story._id} item xs={12} sm={6} md={4}>
            <Card sx={{ height: '100%' }}>
              {story.images && story.images.length > 0 && (
                <Image alt={story.name} src={story.images[0]} ratio="4/3" />
              )}
              <CardContent>
                <Stack
                  direction={'row'}
                  justifyContent={'space-between'}
                  alignItems={'flex-start'}
                  spacing={1}
                >
                  <Stack>
                    <Typography variant="subtitle1">{story.name}</Typography>
                    {story.title && (
                      <Typography variant="caption" color={'text.secondary'}>
                        {story.title}
                      </Typography>
                    )}
                    {(story.type || 'story') === 'activity' && story.date && (
                      <Typography variant="caption" color={'text.secondary'}>
                        {fDate(story.date)}
                      </Typography>
                    )}
                  </Stack>
                  <Stack direction={'row'} spacing={0.5} alignItems={'flex-start'}>
                    <Chip
                      size="small"
                      label={(story.type || 'story') === 'activity' ? 'Activity' : 'Story'}
                      color={(story.type || 'story') === 'activity' ? 'info' : 'warning'}
                    />
                    <Chip
                      size="small"
                      label={story.active ? 'Active' : 'Inactive'}
                      color={story.active ? 'success' : 'default'}
                    />
                  </Stack>
                </Stack>

                <TextMaxLine line={2} variant={'body2'} sx={{ mt: 1, color: 'text.disabled' }}>
                  {story.story}
                </TextMaxLine>

                <Stack
                  direction={'row'}
                  justifyContent={'space-between'}
                  alignItems={'center'}
                  mt={1}
                >
                  <Typography variant="caption" color={'text.secondary'}>
                    Order: {story.order}
                  </Typography>
                  <Stack direction={'row'} spacing={0.5}>
                    <IconButton size="small" onClick={() => handleEditRow(story)}>
                      <Iconify icon={'eva:edit-fill'} />
                    </IconButton>
                    {canDelete && (
                      <IconButton
                        size="small"
                        sx={{ color: 'error.main' }}
                        onClick={() => setConfirmOpen({ open: true, currentId: story._id })}
                      >
                        <Iconify icon={'eva:trash-2-outline'} />
                      </IconButton>
                    )}
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
        {filteredStories.length === 0 && (
          <Stack width={'100%'} height="300px" alignItems={'center'} justifyContent="center">
            <Typography> No success stories yet</Typography>
          </Stack>
        )}
      </Grid>

      <SuccessStoryModal
        open={modalOpen}
        handleClose={() => {
          setModalOpen(false);
          getList();
          setCurrentStory(undefined);
        }}
        isEdit={currentStory ? true : false}
        currentStory={currentStory}
      />

      <ConfirmationDialog
        headText={'Delete Story'}
        text={'Are You Sure You Need To Delete This Story'}
        isOpen={confirmOpen.open}
        onClose={() => {
          setConfirmOpen({ open: false, currentId: '' });
          getList();
        }}
        confirmAction={() => {
          handleDeleteRow(confirmOpen.currentId).then(() => getList());
        }}
        loading={false}
      />
    </Stack>
  );
}
